/**
 * Light, dark or the device's own setting (ADR-042). The choice is kept on this device (a café tablet
 * and the owner's phone each keep their own); "system" follows the device's light or dark setting, also
 * while the app is open. public/theme-init.js applies the same rule before the first paint.
 */
export type ThemePreference = 'light' | 'dark' | 'system';
/** The theme shown. */
export type Theme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'didto-os:theme';
/** The browser's own chrome (status bar, task switcher) matches the page's background (--bg-app). */
export const THEME_CHROME: Record<Theme, string> = { dark: '#101216', light: '#f3f1ec' };
const LIGHT_QUERY = '(prefers-color-scheme: light)';

export function parseThemePreference(v: unknown): ThemePreference {
  return v === 'light' || v === 'dark' || v === 'system' ? v : 'system';
}

/** The stored choice; a device that never chose follows its own setting. */
export function readThemePreference(): ThemePreference {
  try {
    return parseThemePreference(localStorage.getItem(THEME_STORAGE_KEY));
  } catch {
    return 'system';
  }
}

export function storeThemePreference(pref: ThemePreference): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, pref);
  } catch {
    /* storage unavailable: the theme still applies for this visit */
  }
}

/** The device's setting; dark where it cannot be read (the app was dark-first). */
export function systemTheme(): Theme {
  try {
    return typeof window !== 'undefined' && window.matchMedia?.(LIGHT_QUERY).matches
      ? 'light'
      : 'dark';
  } catch {
    return 'dark';
  }
}

export function resolveTheme(pref: ThemePreference, system: Theme = systemTheme()): Theme {
  return pref === 'system' ? system : pref;
}

/** Shows the theme a preference means now; returns it. */
export function applyTheme(pref: ThemePreference): Theme {
  const theme = resolveTheme(pref);
  const root = document.documentElement;
  root.setAttribute('data-theme', theme);
  root.setAttribute('data-theme-preference', pref);
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', THEME_CHROME[theme]);
  return theme;
}

/** Calls back when the device switches between light and dark; returns how to stop. */
export function watchSystemTheme(onChange: (theme: Theme) => void): () => void {
  const mq = typeof window !== 'undefined' ? window.matchMedia?.(LIGHT_QUERY) : undefined;
  if (!mq) return () => undefined;
  const handler = (e: MediaQueryListEvent) => onChange(e.matches ? 'light' : 'dark');
  mq.addEventListener('change', handler);
  return () => mq.removeEventListener('change', handler);
}
